"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  GroupedData,
  GroupedVirtualList,
  GroupedVirtualListRef,
} from "@ospweb/sdk-ui";
import log from "logbeacon";
import { cn, dayjs } from "utils";
import { OtherAlertItem } from "../TimeLineItem";
import { SummaryReportTip } from "../SummaryReportTip";
import { ConditionalInsertOptions } from "../EdggyAgent/EdggyTimeLine/EdggyTimeLine";
import Translations from "@translate/components/CoPilot/CopilotAgent/SummaryAgent/SummaryTimeLine";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";
import CardLoading from "@/src/components/Chat/Cards/CardLoading";
import {
  AgentReport,
  AgentReportLevelEnum,
} from "@/src/features/api/memberApi";
import { EmptyState } from "@/src/components/Empty";

const I18n = I18nUtil.createScoped(Translations);

export interface SummaryTimeLineRef {
  insertItem: (item: AgentReport) => void;
  insertItems: (
    items: AgentReport[],
    options?: ConditionalInsertOptions,
  ) => void;
  scrollToTop: (smooth?: boolean) => void;
  updateItemHeight: (
    itemKey: string,
    height: number,
    isExpanded?: boolean,
  ) => void;
  recalculateHeights: () => void;
}
export interface SummaryTimeLineProps {
  data: AgentReport[];
  hasNextPage: boolean;
  isLoading: boolean;
  isFetching?: boolean;
  isPending: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  agentId?: string;
  onChartClick: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    link?: string;
    type?: string;
  }) => void;
}

export const SummaryTimeLine = forwardRef<
  SummaryTimeLineRef,
  SummaryTimeLineProps
>(
  (
    {
      data,
      hasNextPage,
      isLoading,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      agentId,
      onChartClick,
    }: SummaryTimeLineProps,
    ref,
  ) => {
    const [expandedItemKeys, setExpandedItemKeys] = useState<Set<string>>(
      new Set(),
    );
    const [isPC] = useIsPc();
    const groupedVirtualListRef =
      useRef<GroupedVirtualListRef<AgentReport>>(null);

    /**
     * 将 AgentReportLevelEnum 转换为 OtherAlertItem 需要的 level 格式
     */
    const convertLevel = useCallback(
      (level?: AgentReportLevelEnum): "High" | "Medium" | "Low" => {
        if (level === AgentReportLevelEnum.HIGH) {
          return "High";
        }
        if (level === AgentReportLevelEnum.MID) {
          return "Medium";
        }
        return "Low";
      },
      [],
    );

    /**
     * 将扁平数组转换为分组数据
     * 根据 created 时间戳按日期分组（Today, Yesterday, 或日期格式）
     */
    const groupedData = useMemo<GroupedData<AgentReport>>(() => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const groups: GroupedData<AgentReport> = {};

      data.forEach((item) => {
        if (!item?.id) {
          return;
        }

        let dateKey: string;
        const createdTimestamp = item.created;

        if (!createdTimestamp) {
          // 如果没有创建时间，放在 Today 分组
          dateKey = "Today";
        } else {
          // created 是 int64 时间戳（毫秒或秒），需要转换为 Date
          const itemDate = new Date(
            createdTimestamp > 1000000000000
              ? createdTimestamp
              : createdTimestamp * 1000,
          );

          if (itemDate.toDateString() === today.toDateString()) {
            dateKey = "Today";
          } else if (itemDate.toDateString() === yesterday.toDateString()) {
            dateKey = "Yesterday";
          } else {
            // 格式化日期：MMM DD, YYYY (例如：Jan 15, 2024)
            dateKey = dayjs(itemDate).format("MMM DD, YYYY");
          }
        }

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(item);
      });

      const todayItems = groups["Today"];
      if (todayItems && todayItems.length > 0) {
        const hasSummary = todayItems[0].type === "ALERT_SUMMARY";
        if (!hasSummary) {
          const summaryItem: AgentReport & { isOpen?: boolean } = {
            id: `summary-report-${Date.now()}`,
            agent_id: agentId ?? "",
            content: {
              title: "",
              summary: "",
              thinking: "",
            },
            created: Date.now(),
            level: undefined,
            type: "ALERT_SUMMARY" as AgentReport["type"],
            categories: [],
            isOpen: true,
          };
          todayItems.unshift(summaryItem);
        }
      }
      return groups;
    }, [data, agentId]);

    useEffect(() => {
      if (!groupedData) {
        return;
      }

      const summaryData = localStorage.getItem("is_summary_loaded");
      log.debug(
        "[文案]SummaryTimeLine",
        agentId,
        groupedData,
        summaryData,
        groupedData.Today?.[0]?.id,
      );
      if (
        summaryData === agentId &&
        groupedData.Today?.[0]?.type === "ALERT_SUMMARY"
      ) {
        const itemId = `${groupedData.Today?.[0]?.id ?? ""}`;
        if (itemId) {
          setExpandedItemKeys((prev) => new Set(prev).add(itemId));
        }
        localStorage.removeItem("is_summary_loaded");
      }
      log.debug("[文案]SummaryTimeLine", agentId, groupedData);
    }, [groupedData, agentId]);

    useImperativeHandle(
      ref,
      () => ({
        insertItem: (item: AgentReport, options?: ConditionalInsertOptions) => {
          log.debug("[SummaryAgent][ws] insertItem ===", JSON.stringify(item));

          // 如果没有提供选项，使用默认行为
          if (!options) {
            groupedVirtualListRef.current?.insertItem(item);
            return;
          }

          // 使用条件插入
          groupedVirtualListRef.current?.insertItem(item, {
            position: options.position,
            matcher: options.matcher,
            searchGroups: options.searchGroups,
          });
        },
        insertItems: (
          items: AgentReport[],
          options?: ConditionalInsertOptions,
        ) => {
          log.debug(
            "[SummaryAgent][ws] insertItems ===",
            JSON.stringify(items),
          );
          // 如果没有提供选项，使用默认行为
          if (!options) {
            groupedVirtualListRef.current?.insertItems(items);
            return;
          }

          // 使用条件插入
          groupedVirtualListRef.current?.insertItems(items, {
            position: options.position,
            matcher: options.matcher,
            searchGroups: options.searchGroups,
          });
        },
        scrollToTop: (smooth?: boolean) => {
          groupedVirtualListRef.current?.scrollToTop(smooth);
        },
        updateItemHeight: (
          itemKey: string,
          height: number,
          isExpanded?: boolean,
        ) => {
          groupedVirtualListRef.current?.updateItemHeight(
            itemKey,
            height,
            isExpanded,
          );
        },
        recalculateHeights: () => {
          groupedVirtualListRef.current?.recalculateHeights();
        },
      }),
      [],
    );

    /**
     * 处理滚动到底部，加载更多数据
     */
    const handleEndReached = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
        log.debug("[SummaryTimeLine] onEndReached, fetching next page");
        fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const renderLoading = useMemo(() => {
      if (!hasNextPage || !isFetchingNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }, [hasNextPage, isFetchingNextPage]);

    const renderNoMore = useMemo(() => {
      if (hasNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4 pb-20">
          <span className="body_s text-content_quaternary">
            {I18n.t("No more data")}
          </span>
        </div>
      );
    }, [hasNextPage]);

    /**
     * 动态计算列表项高度
     */
    const getItemHeight = useCallback(
      (_item: AgentReport, isExpanded: boolean) => {
        // OtherAlertItem 的展开和折叠高度可以根据实际情况调整
        if (isExpanded) {
          return 150;
        }
        return 80;
      },
      [],
    );

    const renderHeader = useCallback((groupKey: string) => {
      return (
        <div className="flex items-center h-5 bg-background_solid_secondary w-full">
          <span className="body_s text-content_tertiary">{groupKey}</span>
        </div>
      );
    }, []);

    /**
     * 渲染时间线项
     */
    const renderTimelineItem = useCallback(
      (
        item: AgentReport,
        _index: number,
        _isExpanded: boolean,
        _toggleExpand: () => void,
      ) => {
        // 获取标题：优先使用 content?.title，否则使用 agent?.name
        const title = item.content?.title || "";

        // 获取分类：优先使用 categories[0]，否则使用默认值
        const category = item.categories?.[0] || "STOCK";

        // 获取摘要
        const summary = item.content?.description || "";
        // 获取 impact
        const impact = item.content?.impact || "";

        // 转换 level
        const level = convertLevel(item.level);

        // 获取创建时间
        const created = item.created || Date.now();

        if (item.type === "ALERT_SUMMARY") {
          return (
            <SummaryReportTip
              agentId={agentId}
              id={item.id}
              content={{
                summary: item.content?.summary || "",
                title: title,
              }}
              created={created}
              isOpen={_isExpanded}
              onOpenChange={_toggleExpand}
              onChatClick={onChartClick}
            />
          );
        }

        return (
          <OtherAlertItem
            title={title}
            id={item.id ?? ""}
            category={category}
            summary={summary}
            impact={impact}
            level={level}
            created={created}
            agentId={item?.agent_id}
            sourceLink={item?.source_link ?? ""}
            onChartClick={onChartClick}
            isOpen={_isExpanded}
            onOpenChange={_toggleExpand}
            assets={item?.assets}
          />
        );
      },
      [convertLevel, agentId, onChartClick],
    );

    const handleGetItemKey = useCallback(
      (item: AgentReport) => `${item?.id ?? ""}_${item?.agent_id ?? ""}`,
      [],
    );

    const handleGetItemHeight = useCallback(
      (item: AgentReport) => getItemHeight(item, false),
      [getItemHeight],
    );

    const handleItemToggle = useCallback((key: string, isExpanded: boolean) => {
      setExpandedItemKeys((prev) => {
        const next = new Set(prev);
        if (isExpanded) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    }, []);

    // 初始加载状态：没有数据且正在加载时显示全屏 loading
    const isInitialLoading = (isLoading || isFetching) && data.length === 0;

    if (isInitialLoading) {
      return (
        <div className="flex flex-col w-full h-[calc(100vh-200px)] items-center justify-center">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }

    //空状态：没有数据且不在加载时显示空状态提示
    if (data.length === 0 && !isLoading) {
      return (
        <div className="flex flex-col w-full md:h-[calc(100vh-268px)] h-[calc(100vh-288px)] items-center justify-center">
          <EmptyState
            title={I18n.t("No Smart Signals Yet")}
            description={I18n.t(
              "Check other agents, I’ll alert you when this one finds something worth your attention.",
            )}
            className="w-full max-w-[320px]"
          />
        </div>
      );
    }
    // 有数据时显示列表
    return (
      <div
        className="flex flex-col relative"
        data-tracking-scope="summary-time-line"
      >
        <GroupedVirtualList
          ref={groupedVirtualListRef}
          data={groupedData}
          getItemKey={handleGetItemKey}
          itemGap={isPC ? 16 : 12}
          groupGap={8}
          groupToItemGap={8}
          getItemHeight={handleGetItemHeight}
          enableAutoHeightDetection={true}
          groupHeaderHeight={24}
          stickyTopOffset={0}
          hasNextPage={hasNextPage ?? false}
          onEndReached={handleEndReached}
          onEndReachedThreshold={40}
          renderItem={renderTimelineItem}
          renderGroupHeader={renderHeader}
          renderLoading={renderLoading}
          renderNoMore={renderNoMore}
          expandedItemKeys={expandedItemKeys}
          onItemToggle={handleItemToggle}
          className={cn("md:h-[calc(100vh-208px)] h-[calc(100vh-248px)]")}
          showBackToTop={true}
        />
      </div>
    );
  },
);

SummaryTimeLine.displayName = "SummaryTimeLine";
